import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useLeadList,
  useLeadStatuses,
  type LeadListParams,
} from "../hooks/useLeads";
import { NewLeadDrawer } from "../components/NewLeadDrawer";
import { LeadStatusBadge } from "../components/LeadStatusBadge";

type TabKey = "all" | "new" | "mine" | "customers" | "lost";

const TABS: { key: TabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "mine", label: "Mine" },
  { key: "customers", label: "Customers" },
  { key: "lost", label: "Lost" },
];

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB");
}

export default function LeadsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = !!user?.permissions?.leads?.canCreate;

  const [tab, setTab] = useState<TabKey>("all");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [owner, setOwner] = useState("");
  const [hasEnq, setHasEnq] = useState<"" | "yes" | "no">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const users = useUserList({ limit: 100, status: "ACTIVE" });
  const { data: statuses = [] } = useLeadStatuses();

  const params = useMemo<LeadListParams>(
    () => ({
      page,
      limit,
      tab,
      search: search.trim() || undefined,
      status: status || undefined,
      ownerId: owner || undefined,
      hasEnquiries: hasEnq || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [page, limit, tab, search, status, owner, hasEnq, dateFrom, dateTo],
  );

  const { data, isLoading, isError, error } = useLeadList(params);
  const rows = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const summary = data?.summary;

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "CRM" },
        { label: "Leads" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Leads</h1>
            {summary && (
              <span className="text-sm text-muted-foreground">
                {summary.new} new · {summary.customers} customers · {summary.lost} lost
              </span>
            )}
          </div>
          {canCreate && (
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Lead
            </Button>
          )}
        </div>

        <div className="border-b border-border">
          <nav className="-mb-px flex gap-4 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                  setPage(1);
                }}
                className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                  tab === t.key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-[260px]">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              placeholder="Search name, email, phone, company…"
              value={search}
              onChange={(e) => resetPage(setSearch)(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={status}
            onChange={(e) => resetPage(setStatus)(e.target.value)}
            className="w-40"
          >
            <option value="">All Status</option>
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <Select
            value={owner}
            onChange={(e) => resetPage(setOwner)(e.target.value)}
            className="w-44"
          >
            <option value="">All Owners</option>
            {users.data?.data.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
          <Select
            value={hasEnq}
            onChange={(e) => resetPage(setHasEnq)(e.target.value as "" | "yes" | "no")}
            className="w-44"
          >
            <option value="">Any Enquiries</option>
            <option value="yes">Has enquiries</option>
            <option value="no">No enquiries</option>
          </Select>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => resetPage(setDateFrom)(e.target.value)}
            className="w-40"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => resetPage(setDateTo)(e.target.value)}
            className="w-40"
          />
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            Failed to load leads: {(error as Error)?.message}
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-xl border bg-card"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Loading leads…
              </div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                No leads match the current filters.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3">Code</th>
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3">Email</th>
                    <th className="px-6 py-3">Phone</th>
                    <th className="px-6 py-3">Company</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Owner</th>
                    <th className="px-6 py-3 text-right">Enq</th>
                    <th className="px-6 py-3">Last Enquiry</th>
                    <th className="px-6 py-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((l) => (
                    <tr
                      key={l.id}
                      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                      onClick={() => navigate(`/leads/${l.id}`)}
                    >
                      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                        {l.code}
                      </td>
                      <td className="px-6 py-4 text-foreground">
                        {l.name ?? "—"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {l.email ?? "—"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {l.phone ?? "—"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {l.companyName ?? "—"}
                      </td>
                      <td className="px-6 py-4">
                        <LeadStatusBadge status={l.status} />
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {l.ownerName ?? "—"}
                      </td>
                      <td className="px-6 py-4 text-right text-muted-foreground">
                        {l.enquiryCount}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {relativeTime(l.lastEnquiryAt)}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {relativeTime(l.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div
              className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground"
              style={{
                borderTop: "1px solid hsl(210,30%,85%)",
                background: "hsl(210,30%,97%)",
              }}
            >
              <div className="flex items-center gap-2">
                <span>
                  {total === 0 ? 0 : (page - 1) * limit + 1}–
                  {Math.min(page * limit, total)} of {total}
                </span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-[26px] rounded border bg-white px-1"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded border px-2 py-1 disabled:opacity-40"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                >
                  ‹
                </button>
                <span>
                  {Math.ceil(total / limit) === 0 ? 0 : page} /{" "}
                  {Math.ceil(total / limit) || 1}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(total / limit)}
                  className="rounded border px-2 py-1 disabled:opacity-40"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}

        <NewLeadDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onCreated={(lead) => navigate(`/leads/${lead.id}`)}
        />
      </div>
    </AppLayout>
  );
}
